"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

import { track } from "@/lib/analytics";
import { gaEvent, metaTrackCustom } from "@/lib/analytics/clients";
import { MICROCOPY_POOL } from "@/lib/content/homepage-variants";
import { QuestionOption } from "@/components/quiz/question-option";
import { FinishingProgress } from "@/components/quiz/finishing-progress";
import { IncomeQuestionCard } from "@/components/quiz/income-question-card";
import {
  INCOME_CARD_AFTER_INDEX,
  type IncomeBucket,
} from "@/lib/quiz/income-question";
import {
  QUESTION_BANK,
  TOTAL_QUESTIONS,
  type Question,
} from "@/lib/quiz/question-bank";
import { milestoneBannerForIndex } from "@/lib/content/quiz-milestones";
import {
  CAPTION_ROTATE_MS,
  captionForElapsed,
  progressForElapsed,
} from "@/lib/quiz/finishing-state";

/**
 * Sprint 5 quiz player — one question per screen, real persistence,
 * resilient resume via localStorage. The Sprint 2 state machine and API
 * contracts are preserved verbatim; only the presentation is re-skinned to
 * the Sprint 5 design (warm card surfaces, sparrow mascot, milestone
 * banners). Questions are forward-only — there is no back button.
 *
 * Source-of-truth docs:
 *   - docs/02-product/quiz-framework.md (UX rules)
 *   - docs/02-product/question-bank.md  (the 20 questions)
 *   - docs/04-technical/api-quiz.md     (route contracts)
 *
 * What's wired (unchanged):
 *   - POST /api/quiz/start with attribution from ?ev / ?ea
 *   - POST /api/quiz/save-answer (per answer)
 *   - POST /api/quiz/finish, then router.push("/result/<token>")
 *   - localStorage(`mi:quiz`) for resume after refresh / accidental back
 *
 * Analytics fired client-side (mirrored server-side by the routes):
 *   - quiz_started (route also fires it server-side once the row exists)
 *   - quiz_question_viewed (client)
 *   - quiz_answer_selected (client)
 *   - quiz_abandoned (client, on tab unload mid-quiz)
 */

/** Between-question sparrow "reading your answer" beat (300–500ms). */
const TRANSITION_MS = 420;
const STORAGE_KEY = "mi:quiz";
/**
 * Mirror of MIN_DURATION_S in /api/quiz/finish — MUST stay in lockstep with it
 * (a mismatch makes the server 422 fast clients with `submission_too_fast`).
 * Client buffers so a fast user never sees that raw error — they see a calm
 * "reading your answers" state for the remaining seconds instead.
 *
 * Sprint 15 hotfix (2026-06-14): lowered 60s → 6s. The original 60s buffer was
 * the dominant cause of "NAGYON sokáig tart" — a user who finished in 30s sat
 * staring at a frozen-looking 100% bar for the remaining 30s. 6s is enough to
 * play the reasoning-step captions without feeling broken.
 */
const MIN_DURATION_MS = 6_000;

/**
 * Q1 (Sprint 19): how long the inter-question milestone banner auto-displays
 * before it fades out and advances to the next question.
 */
const MILESTONE_DISPLAY_MS = 3_000;
/**
 * Q1 (Sprint 19): opacity fade-out duration on the milestone banner (ms).
 * Matches the CSS transition duration set inline on the banner wrapper.
 */
const MILESTONE_FADE_MS = 250;

/**
 * Q2 (Sprint 19): minimum time the finishing/loading animation is shown from
 * the moment `finishing` becomes true (i.e. after the last question). This
 * ensures the 6-second loading animation plays in full even when the
 * /api/quiz/finish API responds instantly.
 */
const MIN_LOADING_MS = 6_000;

/** Hard cap on the /api/quiz/finish POST so a hung request surfaces retry UI. */
const FINISH_FETCH_TIMEOUT_MS = 15_000;

const SPARROW_SRC = "/illustrations/sparrow.png";

/**
 * Microcopy shown under the CTA on a normal (non-milestone) question.
 * Rotates deterministically through the pool by question index so the line
 * is stable per screen but varies as the user progresses. Milestone lines
 * are handled separately by the inter-question banner.
 */
function microcopyForIndex(index: number): string {
  const pool = MICROCOPY_POOL;
  const entry = pool[(index - 1) % pool.length];
  return entry ? entry.line : "";
}

type AnswerState = Record<string, number | undefined>;

interface PersistedState {
  token: string;
  startedAt: number;
  answers: AnswerState;
  currentIdx: number;
  /** F1: true once the mid-quiz income card has been submitted/seen. */
  incomeDone?: boolean;
}

function readPersisted(): PersistedState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedState;
    if (typeof parsed?.token !== "string") return null;
    // Clamp a stale/corrupt currentIdx into range so a resuming player can
    // never be stranded on the loader with QUESTION_BANK[currentIdx] undefined.
    const lastIdx = QUESTION_BANK.length - 1;
    const idx = typeof parsed.currentIdx === "number" ? parsed.currentIdx : 0;
    parsed.currentIdx = Math.min(Math.max(0, idx), lastIdx);
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(state: PersistedState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / privacy mode — fall back to in-memory only.
  }
}

function clearPersisted(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/** What the stage is currently showing. */
type Phase = "question" | "transition" | "milestone" | "income";

export function QuizPlayer() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [token, setToken] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<AnswerState>({});
  const [pending, setPending] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>("question");
  const [milestoneLine, setMilestoneLine] = useState<string | null>(null);
  const [incomeDone, setIncomeDone] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  /** Q2 (Sprint 19): timestamp when finishing state began, for MIN_LOADING_MS guard. */
  const finishingStartedAtRef = useRef<number>(0);
  const startedAtRef = useRef<number>(0);
  const sentStartEventRef = useRef(false);
  const sentLoadingStartedRef = useRef(false);
  const sentLoadingResolvedRef = useRef(false);
  /** Q1 (Sprint 19): controls opacity fade-out of the milestone banner. */
  const [milestoneVisible, setMilestoneVisible] = useState(true);

  // ---- prefers-reduced-motion ----
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(m.matches);
    sync();
    m.addEventListener("change", sync);
    return () => m.removeEventListener("change", sync);
  }, []);

  // ---- start (or resume) a submission once on mount ----
  useEffect(() => {
    let cancelled = false;
    const persisted = readPersisted();
    if (persisted && persisted.token) {
      setToken(persisted.token);
      setAnswers(persisted.answers ?? {});
      setCurrentIdx(persisted.currentIdx ?? 0);
      setIncomeDone(persisted.incomeDone ?? false);
      startedAtRef.current = persisted.startedAt ?? Date.now();
      return;
    }
    const entryVariant = searchParams.get("ev");
    const entryAngle = searchParams.get("ea");
    const utmSource = searchParams.get("utm_source");
    const utmMedium = searchParams.get("utm_medium");
    const utmCampaign = searchParams.get("utm_campaign");

    void fetch("/api/quiz/start", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        entry_variant_id: entryVariant ?? null,
        entry_angle:
          entryAngle && ["money-leaks", "month-end", "archetype", "save-more"].includes(entryAngle)
            ? entryAngle
            : null,
        source: utmSource ?? null,
        utm_medium: utmMedium ?? null,
        utm_campaign: utmCampaign ?? null,
        referrer:
          typeof document !== "undefined" && document.referrer
            ? document.referrer.slice(0, 2048)
            : null,
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`start_${res.status}`);
        const json = (await res.json()) as { token: string };
        if (cancelled) return;
        setToken(json.token);
        startedAtRef.current = Date.now();
        writePersisted({
          token: json.token,
          startedAt: startedAtRef.current,
          answers: {},
          currentIdx: 0,
        });
        if (!sentStartEventRef.current) {
          sentStartEventRef.current = true;
          track("quiz_started", {
            entry_variant_id: entryVariant ?? "",
            entry_angle: entryAngle ?? "",
          });
          // Meta Pixel + GA4 funnel mirrors (best-effort, no-op when absent).
          metaTrackCustom("QuizStart", {
            entry_variant_id: entryVariant ?? "",
            entry_angle: entryAngle ?? "",
          });
          gaEvent("quiz_start", {
            entry_variant_id: entryVariant ?? "",
            entry_angle: entryAngle ?? "",
          });
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setStartError(e instanceof Error ? e.message : "start_failed");
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const question: Question | undefined = QUESTION_BANK[currentIdx];

  // ---- analytics: quiz_question_viewed per screen ----
  useEffect(() => {
    if (!question || phase !== "question") return;
    track("quiz_question_viewed", {
      q_id: question.id,
      q_index: currentIdx + 1,
    });
  }, [question, currentIdx, phase]);

  // ---- loading-state telemetry (P0 diagnostics, Sprint 15 hotfix) ----
  // The 2026-06-13 report said the bug also strands users on the "Setting up
  // your test…" (!token) screen. Emitting a started→resolved pair makes a
  // stuck loader visible in the funnel: a `quiz_loading_started` with no
  // matching `quiz_loading_resolved` is the red flag.
  useEffect(() => {
    if (!sentLoadingStartedRef.current && !token) {
      sentLoadingStartedRef.current = true;
      track("quiz_loading_started", {});
    }
    if (!sentLoadingResolvedRef.current && token) {
      sentLoadingResolvedRef.current = true;
      track("quiz_loading_resolved", {});
    }
  }, [token]);

  // ---- abandon detection ----
  useEffect(() => {
    function handler() {
      const finished = Object.keys(answers).length >= TOTAL_QUESTIONS;
      if (finished) return;
      track("quiz_abandoned", {
        last_q_index: currentIdx + 1,
        time_in_quiz_ms: Date.now() - startedAtRef.current,
      });
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [answers, currentIdx]);

  const progressPct = useMemo(() => {
    const answered = Object.keys(answers).length;
    return Math.min(100, Math.round((answered / TOTAL_QUESTIONS) * 100));
  }, [answers]);

  const finish = useCallback(
    async (t: string) => {
      // Q2 (Sprint 19): record when the finishing/loading animation begins so
      // we can guarantee it plays for MIN_LOADING_MS regardless of how fast the
      // API responds. Only stamp on the first call (retry re-enters finish()).
      if (!finishingStartedAtRef.current) {
        finishingStartedAtRef.current = Date.now();
      }
      setFinishing(true);
      setFinishError(null);
      track("quiz_finishing_started", {});

      // Honor the server-side minimum without ever showing the user a raw
      // error: if they ripped through faster, we wait the remainder
      // client-side. The progress bar already reads 100% at this point.
      const elapsed = Date.now() - (startedAtRef.current || Date.now());
      const waitMs = Math.max(0, MIN_DURATION_MS - elapsed);
      if (waitMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      }

      // Hard timeout on the finish POST: a hung request (slow Supabase, Brevo
      // stall in the handler, edge runtime stall) used to leave the user at a
      // frozen 100% bar forever. On abort we surface the FinishingProgress
      // retry UI instead, and the retry re-enters finish() with a fresh
      // controller.
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        FINISH_FETCH_TIMEOUT_MS,
      );
      track("quiz_finishing_fetch_started", {});
      try {
        // Q2 (Sprint 19): enforce MIN_LOADING_MS from the moment the finishing
        // screen appeared. We run the API fetch and the minimum-display timer in
        // parallel via Promise.all — navigation only happens once BOTH resolve.
        const loadingElapsed = Date.now() - (finishingStartedAtRef.current || Date.now());
        const minDisplayWaitMs = Math.max(0, MIN_LOADING_MS - loadingElapsed);

        const [res] = await Promise.all([
          fetch("/api/quiz/finish", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ token: t }),
            signal: controller.signal,
          }),
          new Promise<void>((resolve) => window.setTimeout(resolve, minDisplayWaitMs)),
        ]);
        window.clearTimeout(timeoutId);
        if (!res.ok) {
          track("quiz_finishing_fetch_failed", { reason: `http_${res.status}` });
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `finish_${res.status}`);
        }
        track("quiz_finishing_fetch_ok", {});
        track("quiz_finished", {});
        clearPersisted();
        track("quiz_finishing_routed", {});
        router.push(`/result/${t}`);
      } catch (e) {
        window.clearTimeout(timeoutId);
        const isAbort = e instanceof DOMException && e.name === "AbortError";
        if (isAbort) {
          track("quiz_finishing_fetch_failed", { reason: "timeout" });
        } else if (e instanceof TypeError) {
          // fetch() rejects with TypeError on a network-layer failure.
          track("quiz_finishing_fetch_failed", { reason: "network" });
        }
        const msg = isAbort
          ? "finish_timeout"
          : e instanceof Error
            ? e.message
            : "finish_failed";
        setFinishError(msg);
        setFinishing(false);
      }
    },
    [router],
  );

  /** Move from the current question to whatever comes next. */
  const goToNext = useCallback(
    (nextAnswers: AnswerState, incomeSeen?: boolean) => {
      if (!token) return;
      const next = currentIdx + 1;
      const doneIncome = incomeSeen ?? incomeDone;
      writePersisted({
        token,
        startedAt: startedAtRef.current,
        answers: nextAnswers,
        currentIdx: next,
        incomeDone: doneIncome,
      });
      if (next >= QUESTION_BANK.length) {
        void finish(token);
        return;
      }
      setCurrentIdx(next);
      setPhase("question");
    },
    [token, currentIdx, finish, incomeDone],
  );

  function selectAnswer(value: number) {
    if (!question || pending !== null || phase !== "question" || !token) return;
    setPending(value);
    const nextAnswers: AnswerState = { ...answers, [question.id]: value };
    setAnswers(nextAnswers);
    track("quiz_answer_selected", {
      q_id: question.id,
      q_index: currentIdx + 1,
      value,
    });

    // Fire-and-forget. We do not block UX on the network.
    void persistAnswer(question, value);

    const answeredIndex = currentIdx + 1;
    const banner = milestoneBannerForIndex(answeredIndex);

    // Milestone reached: show the motivational banner. It auto-dismisses
    // after MILESTONE_DISPLAY_MS (Q1, Sprint 19). Reduced motion still shows it
    // (it's content, not decoration) but without the slide animation.
    if (banner) {
      setMilestoneLine(banner);
      setPending(null);
      setPhase("milestone");
      return;
    }

    // F1: the non-scored income card slots in after a fixed mid-quiz scored
    // question (low-friction ordering — past the opener, before fatigue). It
    // is shown once; `incomeDone` (persisted) guards refresh/resume. The
    // user submits it explicitly, which then advances to the next question.
    if (!incomeDone && answeredIndex === INCOME_CARD_AFTER_INDEX) {
      setPending(null);
      setPhase("income");
      return;
    }

    // Reduced motion: snap straight to the next question, no sparrow beat.
    if (reduceMotion) {
      setPending(null);
      goToNext(nextAnswers);
      return;
    }

    // Otherwise play the brief sparrow "reading your answer" transition.
    setPhase("transition");
    window.setTimeout(() => {
      setPending(null);
      goToNext(nextAnswers);
    }, TRANSITION_MS);
  }

  /**
   * Q1 (Sprint 19): auto-dismiss the milestone interstitial.
   * When the milestone phase becomes active, wait MILESTONE_DISPLAY_MS, then
   * fade out (MILESTONE_FADE_MS), then advance. Cleanup prevents double-advance
   * if the component unmounts or the phase changes mid-timer.
   */
  useEffect(() => {
    if (phase !== "milestone") return;
    setMilestoneVisible(true);
    const fadeTimerId = window.setTimeout(() => {
      setMilestoneVisible(false);
    }, MILESTONE_DISPLAY_MS);
    const advanceTimerId = window.setTimeout(() => {
      setMilestoneLine(null);
      goToNext(answers);
    }, MILESTONE_DISPLAY_MS + MILESTONE_FADE_MS);
    return () => {
      window.clearTimeout(fadeTimerId);
      window.clearTimeout(advanceTimerId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  function submitIncome(income: IncomeBucket, partner: IncomeBucket | null) {
    setIncomeDone(true);
    track("quiz_income_answered", {
      bucket: income,
      partner_bucket: partner ?? "",
    });
    void persistIncome(income, partner);
    goToNext(answers, true);
  }

  async function persistIncome(income: IncomeBucket, partner: IncomeBucket | null) {
    if (!token) return;
    // Fire-and-forget, like persistAnswer — never block UX on the network.
    await fetch("/api/quiz/income", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        token,
        income_bucket: income,
        partner_income_bucket: partner,
      }),
    });
  }

  async function persistAnswer(q: Question, value: number) {
    if (!token) return;
    await fetch("/api/quiz/save-answer", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        token,
        q_id: q.id,
        type: q.type,
        value,
      }),
    });
  }

  if (startError) {
    return (
      <QuizShell progressPct={0}>
        <div className="quiz-card mx-auto mt-10 max-w-column p-6 text-center sm:p-8">
          <h1 className="text-lead font-bold leading-tight text-ink md:text-lead-lg">
            We hit a snag starting your test.
          </h1>
          <p className="mx-auto mt-3 max-w-prose text-sub text-mute">
            Refresh the page and try again. If it keeps happening, head back home
            and let us know.
          </p>
          <div className="mt-6">
            <Link href="/spending-personality-test" className="btn-primary">
              Back to the homepage
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </QuizShell>
    );
  }

  // Finishing buffer: the 60s server-side minimum is honored client-side.
  // Instead of a silent static caption we run an informative rotating-caption
  // + animated progress state so the wait never reads as a frozen page
  // (F8, user symptom 2026-06-13). The sparrow still anchors the stage.
  if (finishing) {
    return (
      <QuizFinishingState
        reduceMotion={reduceMotion}
        finishError={finishError}
        onRetry={() => token && void finish(token)}
      />
    );
  }

  // Initial load (no token yet): the sparrow IS the loader, by design. F4
  // gives it the "attentive advisor" head-nod so it reads as leaning in.
  if (!token || !question) {
    return (
      <QuizShell progressPct={0}>
        <SparrowStage
          caption="Setting up your test…"
          reduceMotion={reduceMotion}
          sparrowClass="s5-sparrow-attentive"
        />
      </QuizShell>
    );
  }

  // Between-question sparrow beat.
  if (phase === "transition") {
    return (
      <QuizShell progressPct={progressPct}>
        <SparrowStage caption="Reading your answer…" reduceMotion={reduceMotion} />
      </QuizShell>
    );
  }

  // F1: non-scored mid-quiz income card.
  if (phase === "income") {
    return (
      <QuizShell progressPct={progressPct}>
        <IncomeQuestionCard onSubmit={submitIncome} reduceMotion={reduceMotion} />
      </QuizShell>
    );
  }

  // Inter-question milestone banner.
  // Q1 (Sprint 19): auto-dismisses after MILESTONE_DISPLAY_MS + MILESTONE_FADE_MS.
  // The Continue button is removed; the banner fades out via opacity transition
  // and the useEffect above advances to the next question after the fade.
  if (phase === "milestone" && milestoneLine) {
    return (
      <QuizShell progressPct={progressPct}>
        <div className="mx-auto mt-6 max-w-column">
          <div
            className={`quiz-banner ${reduceMotion ? "" : "animate-banner-drop"}`}
            role="status"
            aria-live="polite"
            style={{
              opacity: milestoneVisible ? 1 : 0,
              transition: reduceMotion ? undefined : `opacity ${MILESTONE_FADE_MS}ms ease-out`,
            }}
          >
            <div className="flex justify-center">
              <Image
                src={SPARROW_SRC}
                alt=""
                width={72}
                height={72}
                className={`quiz-sparrow !h-[72px] !w-[72px] ${
                  reduceMotion ? "" : "animate-sparrow-bounce"
                }`}
                priority
              />
            </div>
            <p className="mt-3 text-lead text-ink">{milestoneLine}</p>
          </div>
        </div>
      </QuizShell>
    );
  }

  const selectedValue = answers[question.id];
  const isPending = pending !== null;
  const isLast = currentIdx + 1 === TOTAL_QUESTIONS;

  return (
    <QuizShell progressPct={progressPct}>
      <div
        key={question.id}
        className={`mx-auto mt-6 max-w-column ${
          reduceMotion ? "" : "animate-slide-in-question"
        }`}
      >
        <div className="quiz-card p-5 sm:p-7">
          <span className="quiz-eyebrow">
            <Image
              src={SPARROW_SRC}
              alt=""
              width={22}
              height={22}
              className="!h-[22px] !w-[22px] rounded-full bg-canvas"
            />
            Take your time. There are no wrong answers.
          </span>

          <h1 className="mt-4 text-lead text-ink md:text-lead-lg">{question.text}</h1>

          <ul className="mt-6 space-y-2.5">
            {question.answers.map((a) => {
              const isSelected = selectedValue === a.value;
              return (
                <li key={a.value}>
                  <QuestionOption
                    numeral={String(a.value)}
                    label={a.label}
                    onSelect={() => selectAnswer(a.value)}
                    selected={isSelected}
                    disabled={isPending && !isSelected}
                    dataAttrs={{
                      "data-selected": isSelected ? "true" : "false",
                      "data-pressing":
                        isPending && isSelected ? "true" : "false",
                      "data-q-id": question.id,
                      "data-q-index": currentIdx + 1,
                      "data-answer-value": a.value,
                    }}
                    trailing={
                      isSelected ? (
                        <span
                          aria-hidden="true"
                          className="text-caption font-semibold text-teal"
                        >
                          {reduceMotion ? "selected" : "✓"}
                        </span>
                      ) : undefined
                    }
                  />
                </li>
              );
            })}
          </ul>

          {/* Reduced motion gets an explicit Continue tap (no auto-advance).
              The CTA copy is locked verbatim to "Continue". */}
          {reduceMotion && selectedValue !== undefined ? (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  if (isLast) {
                    void finish(token);
                    return;
                  }
                  goToNext(answers);
                }}
                className="btn-primary"
              >
                Continue
                <span aria-hidden="true">→</span>
              </button>
            </div>
          ) : null}

          <p className="mt-6 max-w-[44ch] text-caption text-mute">
            {microcopyForIndex(currentIdx + 1)}
          </p>
        </div>
      </div>
    </QuizShell>
  );
}

/**
 * Shared quiz chrome: sparrow + wordmark header, progress bar, exit link.
 * Forward-only — there is intentionally no back control.
 */
function QuizShell({
  children,
  progressPct,
}: {
  children: React.ReactNode;
  progressPct: number;
}) {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-canvas">
      <header className="sticky top-0 z-20 border-b border-hairline bg-canvas/85 backdrop-blur">
        <div className="mx-auto flex max-w-page items-center justify-between gap-3 px-page-x py-3 sm:px-page-x-md">
          <span className="inline-flex items-center gap-2">
            <Image
              src={SPARROW_SRC}
              alt=""
              width={28}
              height={28}
              className="!h-7 !w-7 object-contain"
              priority
            />
            <span className="text-body font-bold tracking-tight text-ink">
              MoneyImprint
            </span>
          </span>
          <Link
            href="/spending-personality-test"
            className="text-caption text-mute hover:text-ink"
          >
            Save &amp; exit
          </Link>
        </div>
        <div
          className="border-t border-hairline"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPct}
          aria-label="Quiz progress"
        >
          <div className="mx-auto flex max-w-page items-center gap-3 px-page-x py-2 sm:px-page-x-md">
            <div className="progress-track flex-1">
              <span
                className="progress-fill"
                style={{ width: `${progressPct}%` }}
                aria-hidden="true"
              />
            </div>
            <span className="text-caption font-semibold text-mute">{progressPct}%</span>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-page flex-1 px-page-x pb-16 pt-2 sm:px-page-x-md">
        {children}
      </main>
    </div>
  );
}

/**
 * F8 — the finishing state shown during the 60s server-side minimum wait.
 *
 * Owns the single setInterval that advances elapsed time; the caption and
 * progress are derived from the pure helpers in lib/quiz/finishing-state.ts.
 * Progress fills 0→95% across the window; the remaining 5% lands when the
 * parent flips out of the `finishing` phase and routes to the result page, so
 * the bar reads "almost done" rather than "stuck".
 *
 * F9 (Sprint 15): the inner stage markup (sparrow + caption + bar + retry) was
 * extracted into <FinishingProgress />. The timer stays here so the QuizShell
 * header bar and the inner bar share one interval (no drift). Reduced motion
 * drops the bar transition + sparrow animation but keeps the copy.
 */
function QuizFinishingState({
  reduceMotion,
  finishError,
  onRetry,
}: {
  reduceMotion: boolean;
  finishError: string | null;
  onRetry: () => void;
}) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, CAPTION_ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  const caption = captionForElapsed(elapsedMs);
  const progressPct = progressForElapsed(elapsedMs, MIN_DURATION_MS);

  return (
    <QuizShell progressPct={progressPct}>
      <FinishingProgress
        caption={caption}
        progressPct={progressPct}
        reduceMotion={reduceMotion}
        finishError={finishError}
        onRetry={onRetry}
      />
    </QuizShell>
  );
}

/**
 * Centered sparrow mascot — the loading state for the quiz. The sparrow IS
 * the loader, by design.
 *
 * `sparrowClass` selects the F4 motion state (Sprint 15): the initial-load
 * stage passes "s5-sparrow-attentive" (the head-nod "attentive advisor"
 * state), while the between-question beat keeps the original
 * "animate-sparrow-bounce". Reduced motion drops the animation either way.
 */
function SparrowStage({
  caption,
  reduceMotion,
  sparrowClass = "animate-sparrow-bounce",
}: {
  caption: string;
  reduceMotion: boolean;
  sparrowClass?: string;
}) {
  return (
    <div className="flex min-h-[60dvh] flex-col items-center justify-center text-center">
      <Image
        src={SPARROW_SRC}
        alt=""
        width={128}
        height={128}
        className={`quiz-sparrow ${reduceMotion ? "" : sparrowClass}`}
        priority
      />
      <p className="mt-6 max-w-prose text-sub text-mute" aria-live="polite">
        {caption}
      </p>
    </div>
  );
}
